import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const VehicleDepreciation = () => {
  // State variables for Vehicle Depreciation calculation
  const [vehicleCost, setVehicleCost] = useState(0); // Cost of vehicle
  const [residualValue, setResidualValue] = useState(0); // Residual value of the vehicle
  const [usefulLife, setUsefulLife] = useState(0); // Useful life of the vehicle (in years)
  const [depreciationResult, setDepreciationResult] = useState(0); // Calculated depreciation

  // Event handlers to update state values
  const handleVehicleCostChange = (e) => setVehicleCost(parseFloat(e.target.value) || 0);
  const handleResidualValueChange = (e) => setResidualValue(parseFloat(e.target.value) || 0);
  const handleUsefulLifeChange = (e) => setUsefulLife(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate depreciation when any input changes
  useEffect(() => {
    if (usefulLife > 0) {
      const calculatedDepreciation = (vehicleCost - residualValue) / usefulLife; // Calculate depreciation per year
      setDepreciationResult(calculatedDepreciation);
    } else {
      setDepreciationResult(0);
    }

        // This will initialize or reinitialize all popovers on the page whenever the component renders
        const popoverTriggerList = [].slice.call(
          document.querySelectorAll('[data-bs-toggle="popover"]')
        );
    
        // Clean up any existing popover instances
        popoverTriggerList.forEach((popoverTriggerEl) => {
          const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
          if (popover) {
            popover.dispose(); // Dispose of existing popover
          }
        });
    
        // Re-initialize popovers
        popoverTriggerList.forEach((popoverTriggerEl) => {
          new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
        });

  }, [vehicleCost, residualValue, usefulLife]);

  return (
    <Layout title="Vehicle Depreciation Calculator" path="vehicle-depreciation-calculator">
      <section className="section-padding" id="depreciation-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Depreciation Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Vehicle Depreciation Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Vehicle Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="vehicleCost" className="form-label">
                        Vehicle Cost
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Vehicle Cost:</strong> Enter the initial purchase cost of the vehicle."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="vehicleCost"
                        placeholder="Enter vehicle cost"
                        value={vehicleCost || ""}
                        onChange={handleVehicleCostChange}
                      />
                    </div>

                    {/* Residual Value Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="residualValue" className="form-label">
                        Residual Value
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Residual Value:</strong> Enter the expected value of the vehicle after the useful life has ended."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="residualValue"
                        placeholder="Enter residual value"
                        value={residualValue || ""}
                        onChange={handleResidualValueChange}
                      />
                    </div>

                    {/* Useful Life Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="usefulLife" className="form-label">
                        Useful Life (in years)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Useful Life:</strong> Enter the number of years the vehicle is expected to be in use."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="usefulLife"
                        placeholder="Enter useful life in years"
                        value={usefulLife || ""}
                        onChange={handleUsefulLifeChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Depreciation per Year:{" "}
                        <span className="fw-bold ms-3">
                          {depreciationResult === 0 ? "0.00" : depreciationResult.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Vehicle Depreciation Calculator
                </h3>

                {/* What is a Vehicle Depreciation Calculator? */}
                <h4 className="mt-3 mb-3">What is a Vehicle Depreciation Calculator?</h4>
                <p>
                  The **Vehicle Depreciation Calculator** helps users estimate the depreciation of a vehicle over time. By inputting the cost of the vehicle, residual value, and useful life, users can calculate how much value the vehicle loses each year.
                </p>

                {/* Why Use a Vehicle Depreciation Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Vehicle Depreciation Calculator?</h4>
                <p>
                  This calculator helps users track the depreciation of their vehicle, plan for future purchases, and manage fleet assets efficiently. It is an essential tool for understanding how much a vehicle loses in value each year.
                </p>

                {/* Benefits of Using a Vehicle Depreciation Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Vehicle Depreciation Calculator</h4>
                <ul>
                  <li><strong>Helps in fleet management and resale planning:</strong> By tracking depreciation, businesses can plan for the future sale of vehicles or purchasing new ones.</li>
                  <li><strong>Supports budgeting for future purchases:</strong> Knowing the depreciation helps in budgeting for replacing vehicles in the future.</li>
                  <li><strong>Tracks asset values:</strong> Provides a clear understanding of the current value of a vehicle over time.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Vehicle Depreciation Calculator?</h4>
                <p>
                  The Vehicle Depreciation Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Transportation companies:</strong> To monitor the depreciation of their fleet of vehicles.</li>
                  <li><strong>Rental agencies:</strong> To track the depreciation of vehicles for rental purposes.</li>
                  <li><strong>Individual car owners:</strong> To understand the depreciation of their personal vehicle over time.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How Does Depreciation Affect the Value of a Vehicle?</h4>
                <p>
                  Depreciation reduces the market value of a vehicle each year, meaning that the older the vehicle, the lower its resale value. It is a key factor in determining how much a vehicle is worth at any given point in time.
                </p>

                <h4 className="mt-4 mb-3">How Can I Minimize Depreciation?</h4>
                <p>
                  Maintaining your vehicle, keeping it clean, avoiding accidents, and limiting excessive mileage can help reduce the rate of depreciation. Additionally, purchasing a vehicle known to retain value well over time can minimize depreciation effects.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default VehicleDepreciation;
